import React, { useState } from 'react'
import { CircularProgress, Menu, MenuItem, IconButton } from '@mui/material'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import ConfirmationDialogComponent from '../common/confirmation-dialog.component'
import { PatientService } from '../../../../services/patient.service'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import { useUpdateDataAction } from '../../../../utils/update-details-hook';

interface IProps {
  input: any
  classes: ClassNameMap
}

const DiagnosisPlanActions: React.FC<IProps> = ({ input, classes }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)
  const [isMoveToScreening, setIsMoveToScreening] = useState('') as any
  const [currentPlanAction, setCurrentPlanAction] = useState(null)
  const [planloading, setPlanLoading] = useState(false)
  const patientService = new PatientService()
  const { updatePatientData } = useUpdateDataAction(input?.patient_id)

  let options = ['Exit without diagnosis']
  if (['confirmatory testing', 'post-diagnosis'].includes(input.currStage))
    options = [...options, 'Move to screening']
  options = options.sort()

  const handleConfirmClose = () => setIsMoveToScreening(null)

  const confirmStatus = () => {
    isMoveToScreening && input?.patient_id && input.plan_id &&
      patientService.resetDiagnosisStage(input?.patient_id, input.plan_id)
        .then((res: any) => {
          if (res.data) {
            updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true });
          }
          handleConfirmClose()
        }).catch((err: any) => {
          handleConfirmClose()
          toast.error(Helper.getErrorText(err), Helper.bottom_center())
        })
  }

  const handleClick = (event: any) => setAnchorEl(event.currentTarget)

  const handleClose = () => {
    setAnchorEl(null)
    setCurrentPlanAction(null)
  }

  const handleMovePostDiagnosis = () => {
    if (input?.problem_id && input?.patient_id) {
      setPlanLoading(true)
      patientService.postDiagnosisPlan(input?.patient_id, input?.problem_id, { 'screening_approval_status': 'Rejected' })
        .then((res: any) => {
          if (res.data)
            updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true });
          setPlanLoading(false)
        }).catch((err: any) => {
          setPlanLoading(false)
          toast.error(Helper.getErrorText(err), Helper.bottom_center())
        })
    }
  }

  const changePrgStage = () => {
    setAnchorEl(null)
    setPlanLoading(true)
    patientService.changeProgramStage(input?.patient_id, input.program,).then((res: any) => {
      if (res && res?.data)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      setPlanLoading(false)
    }).catch((error: any) => {
      setPlanLoading(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const changeStageByName = (stage: any) => {
    setAnchorEl(null)
    setPlanLoading(true)
    patientService.changeStageByName(input?.patient_id, input.program, stage).then((res: any) => {
      if (res && res?.data)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      setPlanLoading(false)
    }).catch((error: any) => {
      setPlanLoading(false)
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    })
  }

  const handlePlanAction = (e: any) => {
    let planAction = e.target.innerText
    setCurrentPlanAction(planAction)

    if (['Move to screening'].includes(planAction))
      setIsMoveToScreening(true)
    else if (['Move to post diagnosis'].includes(planAction))
      handleMovePostDiagnosis()
    else if (['Exit without diagnosis'].includes(planAction)) {
      changeStageByName('exited without diagnosis')
    } else if (planAction === 'Proceed to next step') {
      if (input.currStage === 'screening')
        input.handleScreeningApproval()
      else if (input.currStage === 'confirmatory testing')
        changeStageByName('post-diagnosis')
    }
    handleClose()
    return
  }

  return (<>
    {planloading && <CircularProgress className={classes.planLoading} />}
    <IconButton size='small'
      aria-label="more"
      id="long-button"
      disabled={(planloading || input.isActionItems)}
      aria-controls={open ? 'long-menu' : undefined}
      aria-expanded={open ? 'true' : undefined}
      aria-haspopup="true"
      onClick={handleClick}
      className={classes.zeroPadding}
      sx={{ marginLeft: '0rem !important' }}
    >
      <MoreVertOutlinedIcon sx={{ fontSize: '0.85rem !important', padding: '0rem !important' }} />
    </IconButton>
    <Menu
      id="long-menu"
      MenuListProps={{
        'aria-labelledby': 'long-button',
      }}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      sx={{
        '& .Mui-disabled': {
          opacity: '1 !important'
        }
      }}
    >
      {options?.map((option) => (
        <MenuItem
          key={option}
          disabled={(input.currStage === 'screening' && ['Proceed to next step'].includes(option) &&
            input.pending_count > 1 && input.needs_update
          ) ||
            (['Exit without diagnosis'].includes(option) && input.pending_count > 1)
            // ||
            // (input.currStage === 'confirmatory testing' && input.task_pending_count > 1 &&
            //   ['Proceed to next step'].includes(option)
            // )
          }
          value={option}
          selected={option === currentPlanAction}
          onClick={handlePlanAction}
          className={classes.desc}
          sx={{
            '&.Mui-disabled': {
              color: 'grey !important',
            },
            '&.MuiMenuItem-root, & .MuiListItemIcon-root': {
              padding: '0.3rem  0.5rem !important',
            },
          }}
        >
          {option}
        </MenuItem>
      ))}
    </Menu>

    {isMoveToScreening !== null &&
      <ConfirmationDialogComponent
        open={Boolean(isMoveToScreening)}
        content={`Are you sure you want to move to screening stage?`}
        handleConfirmClose={handleConfirmClose}
        confirmStatus={confirmStatus}
      />
    }
  </>
  )
}

export default withStyles(stylesheet)(DiagnosisPlanActions)
